import { FormControlLabel, Grid, Radio, RadioGroup } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";

import { useTranslation } from "react-i18next";

import { DropdownGlobal } from "../../../../../components/DropdownGlobal";
import {
  listExpireInHearingFileUrlDays,
  listExpireInHearingFileUrlHours,
} from "../../constants";

interface ExpireInHearingFileUrlProps {
  editedValue: string;
  setEditedValue: (e: any) => void;
  fieldError: boolean;
  selectedExpireInHearingFileUrlName: string;
}

type ExpireConfig = {
  type: "H" | "D";
  value: string;
};

const DEFAULT_EXPIRE_CONFIG: ExpireConfig = { type: "H", value: "1" };

const parseExpireConfig = (value: string): ExpireConfig => {
  if (!value) {
    return DEFAULT_EXPIRE_CONFIG;
  }

  try {
    const parsed = JSON.parse(value);
    if (parsed?.Type && parsed?.Value !== undefined) {
      return {
        type: parsed.Type === "D" ? "D" : "H",
        value: String(parsed.Value),
      };
    }
  } catch {
    // fallback to legacy value
  }

  return { type: "H", value: String(value) };
};

const ExpireInHearingFileUrl = ({
  setEditedValue,
  fieldError,
  selectedExpireInHearingFileUrlName,
}: ExpireInHearingFileUrlProps) => {
  const { t } = useTranslation();
  const [config, setConfig] = useState<ExpireConfig>(() =>
    parseExpireConfig(selectedExpireInHearingFileUrlName),
  );

  useEffect(() => {
    setConfig(parseExpireConfig(selectedExpireInHearingFileUrlName));
  }, [selectedExpireInHearingFileUrlName]);

  const options = useMemo(
    () =>
      config.type === "D"
        ? listExpireInHearingFileUrlDays
        : listExpireInHearingFileUrlHours,
    [config.type],
  );

  const labelPrefix = config.type === "D" ? t("common.days") : t("common.hours");

  const persistValue = useCallback((nextConfig: ExpireConfig) => {
    setConfig(nextConfig);
    setEditedValue(
      JSON.stringify({
        Type: nextConfig.type,
        Value: nextConfig.value,
      }),
    );
  }, [setEditedValue]);

  const handleTypeChange = (nextType: ExpireConfig["type"]) => {
    const nextOptions =
      nextType === "D"
        ? listExpireInHearingFileUrlDays
        : listExpireInHearingFileUrlHours;
    const nextValue =
      nextOptions.find((option) => String(option.id) === config.value)?.id ??
      nextOptions[0]?.id ??
      "1";

    persistValue({
      type: nextType,
      value: String(nextValue),
    });
  };

  return (
    <Grid container spacing={1} style={{ padding: "16px" }}>
      <Grid item xs={12}>
        <RadioGroup
          value={config.type}
          onChange={(e) => {
            handleTypeChange(e.target.value as ExpireConfig["type"]);
          }}
          row
          sx={{
            "& .MuiFormControlLabel-root": { marginRight: "24px" },
            "& .MuiRadio-root": {
              color: "#666666",
              "&.Mui-checked": { color: "#1D9BF0" },
            },
          }}
        >
          <FormControlLabel
            value="H"
            control={<Radio size="small" />}
            label={t("common.hours")}
            sx={{
              "& .MuiFormControlLabel-label": {
                fontSize: "14px",
                color: "#666666",
              },
            }}
          />
          <FormControlLabel
            value="D"
            control={<Radio size="small" />}
            label={t("common.days")}
            sx={{
              "& .MuiFormControlLabel-label": {
                fontSize: "14px",
                color: "#666666",
              },
            }}
          />
        </RadioGroup>
        <DropdownGlobal
          labelName={t("systemConfiguration.expireVideoAccess", { prefix: labelPrefix })}
          options={options}
          error={fieldError}
          setSelected={(value: string | number) => {
            persistValue({
              type: config.type,
              value: String(value),
            });
          }}
          required={false}
          editDropdown={config.value}
          defaultValue={config.value}
        />
      </Grid>
    </Grid>
  );
};

export default ExpireInHearingFileUrl;
